import { FC, useContext } from 'react';
import { useLazyLoadQuery, useFragment, graphql } from 'react-relay';
import { useCheckoutSelector } from '../../../reducers/root';
import { CheckoutServerVarsContext } from '../../shared/CheckoutServerVarsContext';
import CheckoutDeliveryOptionsQueryRenderer from './CheckoutDeliveryOptionsQueryRenderer';
import CheckoutPageHeader from '../../../header/CheckoutPageHeader';
import { CheckoutMobilePageHeader } from '../../../header/CheckoutMobilePageHeader';
import { Checkout404Page } from '../Checkout404Page';

import { hasAnyTradeStatus } from '../../../helpers/checkoutUser';
import { getPartialAddressCheckoutEligibility } from '../../../shared/helpers/partialAddressesCheckoutAbTestHelpers';
import { useBuyerFetchHandler } from '../../../hooks/useBuyerFetchHandler';

import { CheckoutGuestBuyerQueryRenderer_buyer$key } from './__generated__/CheckoutGuestBuyerQueryRenderer_buyer.graphql';
import { CheckoutGuestBuyerQueryRenderer_viewer$key } from './__generated__/CheckoutGuestBuyerQueryRenderer_viewer.graphql';
import { CheckoutGuestBuyerQueryRenderer_item$key } from './__generated__/CheckoutGuestBuyerQueryRenderer_item.graphql';
import { CheckoutGuestBuyerQueryRenderer_emailUser$key } from './__generated__/CheckoutGuestBuyerQueryRenderer_emailUser.graphql';
import { CheckoutGuestBuyerQueryRenderer_preCheckoutTransaction$key } from './__generated__/CheckoutGuestBuyerQueryRenderer_preCheckoutTransaction.graphql';
import { CheckoutGuestBuyerQueryRendererClientQuery } from './__generated__/CheckoutGuestBuyerQueryRendererClientQuery.graphql';

type Props = {
    buyer: CheckoutGuestBuyerQueryRenderer_buyer$key | null;
    viewer: CheckoutGuestBuyerQueryRenderer_viewer$key | null;
    item: CheckoutGuestBuyerQueryRenderer_item$key | null;
    emailUser: CheckoutGuestBuyerQueryRenderer_emailUser$key | null;
    preCheckoutTransaction: CheckoutGuestBuyerQueryRenderer_preCheckoutTransaction$key | null;
};

const buyerFragment = graphql`
    fragment CheckoutGuestBuyerQueryRenderer_buyer on User {
        ...CheckoutDeliveryOptionsQueryRenderer_buyer
        ...CheckoutPageHeader_buyer @skip(if: $isMobile)
        ...CheckoutMobilePageHeader_buyer @include(if: $isMobile)
        ...useBuyerFetchHandler_buyer
        ...useBuyerFetchHandler_fetchedBuyer
    }
`;

const CheckoutGuestBuyerQueryRenderer: FC<Props> = ({
    buyer: buyerRef,
    viewer: viewerRef,
    item: itemRef,
    emailUser: emailUserRef,
    preCheckoutTransaction: preCheckoutTransactionRef,
}) => {
    const buyerId = useCheckoutSelector(state => state.transaction.buyerId);
    const { isMobile } = useContext(CheckoutServerVarsContext);
    const viewer = useFragment(
        graphql`
            fragment CheckoutGuestBuyerQueryRenderer_viewer on Viewer {
                ...CheckoutPageHeader_viewer @skip(if: $isMobile)
                ...CheckoutDeliveryOptionsQueryRenderer_viewer
                ...CheckoutMobilePageHeader_viewer @include(if: $isMobile)
                ...useBuyerFetchHandler_viewer
            }
        `,
        viewerRef
    );

    const item = useFragment(
        graphql`
            fragment CheckoutGuestBuyerQueryRenderer_item on Item {
                ...CheckoutDeliveryOptionsQueryRenderer_item
                ...CheckoutMobilePageHeader_item
                ...useBuyerFetchHandler_item
            }
        `,
        itemRef
    );

    const preCheckoutTransaction = useFragment(
        graphql`
            fragment CheckoutGuestBuyerQueryRenderer_preCheckoutTransaction on Transaction {
                ...CheckoutDeliveryOptionsQueryRenderer_preCheckoutTransaction
                ...useBuyerFetchHandler_preCheckoutTransaction
            }
        `,
        preCheckoutTransactionRef
    );

    const emailUser = useFragment<CheckoutGuestBuyerQueryRenderer_emailUser$key>(
        graphql`
            fragment CheckoutGuestBuyerQueryRenderer_emailUser on User {
                ...useBuyerFetchHandler_emailUser
            }
        `,
        emailUserRef
    );

    const fetchedBuyer = useFragment<CheckoutGuestBuyerQueryRenderer_buyer$key>(
        buyerFragment,
        buyerRef
    );

    const hasTradeStatus =
        useCheckoutSelector(state => state.tradeStatus.hasTradeStatus) || hasAnyTradeStatus();

    const clientData = useLazyLoadQuery<CheckoutGuestBuyerQueryRendererClientQuery>(
        graphql`
            query CheckoutGuestBuyerQueryRendererClientQuery(
                $buyerId: String = ""
                $hasBuyerId: Boolean!
                $isMobile: Boolean!
                $hasTradeStatus: Boolean!
                $partialAddressCheckoutEligibility: String!
            ) {
                viewer {
                    user(userId: $buyerId) @include(if: $hasBuyerId) {
                        ...CheckoutGuestBuyerQueryRenderer_buyer
                    }
                }
            }
        `,
        {
            buyerId,
            hasBuyerId: !!buyerId,
            isMobile,
            hasTradeStatus,
            partialAddressCheckoutEligibility: getPartialAddressCheckoutEligibility(),
        },
        { fetchPolicy: 'store-only' }
    );

    const buyer = useFragment<CheckoutGuestBuyerQueryRenderer_buyer$key>(
        buyerFragment,
        clientData.viewer?.user || null
    );

    useBuyerFetchHandler({
        buyer,
        viewer,
        item,
        preCheckoutTransaction,
        emailUser,
        fetchedBuyer,
    });

    return (
        <>
            {isMobile ? (
                <CheckoutMobilePageHeader viewer={viewer} buyer={buyer} item={item} />
            ) : (
                <CheckoutPageHeader viewer={viewer} buyer={buyer} />
            )}
            {!!viewer && !item ? (
                <Checkout404Page />
            ) : (
                <CheckoutDeliveryOptionsQueryRenderer
                    viewer={viewer}
                    item={item}
                    buyer={buyer}
                    preCheckoutTransaction={preCheckoutTransaction}
                />
            )}
        </>
    );
};

export default CheckoutGuestBuyerQueryRenderer;
